import React, { useRef, useState, ReactElement, useEffect, useMemo } from 'react';
import DockLayout, { TabGroup, LayoutData, BoxData, DockMode, PanelData, TabData } from 'rc-dock';
import useUmami from 'pwa-synergy-umami';
import {
  Component,
  AppLayout,
  Orientation,
  Panel,
  TabPanel,
  WindowApi,
  CustomPosition,
  WindowPosition,
} from '../../types';
import { useInstanceManagemer, api, ComponentSelector } from '../../components';
import '../../styles/rc-dock.css';
import './WindowContainer.css';
import { ComponentPanel } from 'src/components/ComponentSelector';

export interface WindowContainerProps<T extends object> {
  layout: AppLayout;
  components: Map<string, Component<T>>;
  titleBar?: (commands: T) => ReactElement;
  toolBar?: (commands: T) => ReactElement;
  statusBar?: (commands: T) => ReactElement;
  commands: (desktopApi: WindowApi) => T;
  hideTitleBar?: boolean;
  hideToolBar?: boolean;
  hideStatusBar?: boolean;
}

const getGroups = (layout: AppLayout): { [key: string]: TabGroup } => {
  const groups: { [key: string]: TabGroup } = {};
  layout.componentGroups.forEach((group) => {
    groups[group.id] = {
      disableDock: group.disableDock,
      floatable: group.floatable,
      maximizable: group.maximizable,
      tabLocked: group.tabLocked,
      panelExtra: group.panelExtra,
      preferredFloatHeight: group.preferredFloatHeight,
      preferredFloatWidth: group.preferredFloatWidth,
    };
  });
  return groups;
};

const orientationToMode = (orientation: Orientation): DockMode => {
  return orientation === 'Horizontal' ? 'horizontal' : 'vertical';
};

const toTab = <T extends object>(
  id: string,
  components: Map<string, Component<T>>,
  commands: T,
): TabData | undefined => {
  const component = components.get(id);
  return component
    ? {
        id,
        title: component.title,
        group: component.group,
        closable: component.closable,
        minWidth: component.minWidth,
        minHeight: component.minHeight,
        content: () => component.content({ id, commands }),
      }
    : undefined;
};

const toPanel = <T extends object>(
  panel: Panel | TabPanel,
  components: Map<string, Component<T>>,
  commands: T,
  newTab: (panelId: string, id: string, props?: any) => string | undefined
): BoxData | PanelData => {
  const tabPanel = panel as TabPanel;
  if (tabPanel.components) {
    const component = tabPanel.customContentComponent 
      ? components.get(tabPanel.customContentComponent)
      : undefined;
    const panelLock = ( tabPanel.minimumWidth && (component || tabPanel.addableComponents ) )
      ? {
        minWidth: tabPanel.minimumWidth,
        panelExtra: component
          ? () => component.content({ id: tabPanel.customContentComponent!, commands })      
          : () => <ComponentSelector 
                    components={
                      tabPanel.addableComponents?.map( addable => {
                        const component = components.get(addable.componentId);
                        return component 
                          ? {
                            component,
                            panelId: addable.panelId
                          }
                          : {}
                      })
                      .filter( component => component != null) as ComponentPanel[] } 
                    newTab={newTab}
                    commands={commands}    
                    panelId={tabPanel.id}
                  />
      }
      : ( tabPanel.minimumWidth )
        ? {
          minWidth: tabPanel.minimumWidth,
        }
        : undefined;
        
    return {
      id: tabPanel.id,
      size: tabPanel.size,
      tabs: tabPanel.components
        .map((id) => toTab(id, components, commands))
        .filter((component) => component !== undefined)
        .map((component) => component as TabData),
        panelLock
    };
  }
  const boxPanel = panel as Panel;
  return {
    mode: orientationToMode(boxPanel.orientation),
    size: boxPanel.size,
    children: boxPanel.children.map((child) => toPanel(child, components, commands, newTab)),
  };
};

const getLayout = <T extends object>(
  layout: AppLayout,
  components: Map<string, Component<T>>,
  commands: T,
  newTab: (panelId: string, id: string, props?: any) => string | undefined
): LayoutData => {
  const dockbox: BoxData = {
    mode: orientationToMode(layout.panels.orientation),
    children: layout.panels.children.map((child) => toPanel(child, components, commands, newTab)),
  };
  const floatbox: BoxData = {
    mode: 'float',
    children: [],
  };
  return {
    dockbox,
    floatbox,
  };
};

const WindowContainer = <T extends object>(props: WindowContainerProps<T>) => {
  const {
    layout,
    components,
    titleBar,
    toolBar,
    statusBar,
    commands,
    hideTitleBar,
    hideToolBar,
    hideStatusBar,
  } = props;
  const dockLayoutRef = useRef<DockLayout>(null);
  const dockLayoutContainerRef = useRef<HTMLDivElement>(null);
  const [canInstance, getInstance, decrementInstance] = useInstanceManagemer<T>(components);
  const [toolBarHeight, setToolBarHeight] = useState<number>(0);
  const [dockLayoutHeight, setDockLayoutHeight] = useState<number>();
  const resizeObserver = useMemo<ResizeObserver>(
    () =>
      new ResizeObserver((elements) => {
        setDockLayoutHeight(elements[0].target.clientHeight);
      }),
    [],
  );
  const track = useUmami({
    url: '/',
    title: `Windowed-${api.isMobile() ? 'Mobile' : 'Desktop'}`,
    disabledPageView: true,
  });

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const getTab = (id: string, props: any, commands: T): TabData | undefined => {
    track(id, props);
    const component = components.get(id);
    if (component) {
      if (canInstance(id)) {
        const instanceId = component.instances === 1 ? id : `${id}_${getInstance(id)}`;
        return {
          id: instanceId,
          title: component.title,
          group: component.group,
          closable: component.closable,
          minHeight: component.minHeight,
          minWidth: component.minWidth,
          content: () => component.content({ id, instanceId, props, commands }),
        };
      }
    }

    return undefined;
  };

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const openWindow = (
    id: string,
    props: any,
    position?: WindowPosition,
    customPosition?: CustomPosition,
  ): string | undefined => {
    track(id, props);
    if (dockLayoutRef.current && commandImpl) {
      const tab = getTab(id, props, commandImpl);
      if (tab) {
        dockLayoutRef.current.dockMove(
          tab,
          'floatbox',
          'float',
          position === 'Custom' && customPosition
            ? {
                left:
                  customPosition?.left ??
                  (dockLayoutContainerRef.current?.clientWidth
                    ? dockLayoutContainerRef.current?.clientWidth / 2 -
                      (customPosition?.width ?? 0) / 2
                    : 50),
                top:
                  customPosition?.top ??
                  (dockLayoutContainerRef.current?.clientHeight
                    ? dockLayoutContainerRef.current?.clientHeight / 2 -
                      (customPosition?.height ?? 0) / 2
                    : 50),
                height: customPosition.height,
                width: customPosition.width,
              }
            : undefined,
        );
        return tab.id;
      }
    }

    return undefined;
  };

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const newTab = (panelId: string, id: string, props: any): string | undefined => {
    track(id, {
      panelId,
      ...props
    });
    if (dockLayoutRef.current && commandImpl) {
      const tab = getTab(id, props, commandImpl);
      if (tab) {
        dockLayoutRef.current.dockMove(tab, panelId, 'active');
        return tab.id;
      }
    }

    return undefined;
  };

  const closeComponent = (id: string, instance?: string) => {
    track('close', id);
    if (dockLayoutRef.current) {
      const tab = dockLayoutRef.current.find(instance ?? id) as TabData;
      decrementInstance(id);
      if (tab) {
        dockLayoutRef.current.dockMove(tab, '', 'remove');
      }
    }
  };

  const commandImpl = useMemo<T>(
    () =>
      commands({
        openWindow,
        newTab,
        closeComponent,
      }),
    [],
  );

  const layoutData = useMemo<LayoutData>(
    () => getLayout(layout, components, commandImpl, newTab),
    [layout, components, commandImpl],
  );
  const groups = useMemo<{ [key: string]: TabGroup }>(() => getGroups(layout), [layout]);

  const updateToolBarHeight = (divRef: HTMLDivElement) => {
    if (divRef) {
      setToolBarHeight(divRef.clientHeight);
    }
  };

  useEffect(() => {
    if (dockLayoutContainerRef.current) {
      resizeObserver.observe(dockLayoutContainerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [resizeObserver]);

  return (
    <div className="windowContainer">
      <div className="windowToolBar" ref={updateToolBarHeight}>
        {!hideTitleBar && titleBar && titleBar(commandImpl)}
        {!hideToolBar && toolBar && toolBar(commandImpl)}
      </div>
      <div className="dockLayoutContainer" ref={dockLayoutContainerRef}>
        <DockLayout
          ref={dockLayoutRef}
          defaultLayout={layoutData}
          groups={groups}
          style={{
            position: 'absolute',
            left: 0,
            top: toolBarHeight ?? 0,
            right: 0,
            bottom: 0,
            height: dockLayoutHeight ?? 0,
          }}
        />
      </div>
      <div className="windowStatusBar">{!hideStatusBar && statusBar && statusBar(commandImpl)}</div>
    </div>
  );
};

export default WindowContainer;
